import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Play, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel, Stat } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { AuditTrail } from "@/components/AuditTrail";
import { BrandKit } from "@/components/BrandKit";
import { AdminDataRequests } from "@/components/AdminDataRequests";
import { AdminEnquiries } from "@/components/AdminEnquiries";
import { EnterpriseCell } from "@/components/EnterpriseCell";
import { AdminUsage } from "@/components/AdminUsage";
import { AdminEmails } from "@/components/AdminEmails";
import { AdminIdPolicy } from "@/components/IdPolicy";
import { MfaResetCell } from "@/components/SecurityPanel";
import { api, fmtDate, gbp, formatError } from "@/lib/api";

const Table = ({ cols, rows, testId }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
    <table className="w-full text-sm" data-testid={testId}>
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
        {cols.map(([h]) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
      </tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || r.session_id || i} className="border-b last:border-0 border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
            {cols.map(([h, fn]) => <td key={h} className="px-4 py-3 whitespace-nowrap">{fn(r)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
    {!rows.length && <p className="text-sm text-slate-500 p-6 text-center">No records.</p>}
  </div>
);

const mono = (v) => <span className="font-mono text-xs">{v}</span>;

const Overview = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/stats").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/lifecycle/run");
      toast.success(`Lifecycle run: ${data.reminders} reminder(s), ${data.returned} returned`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!s) return <Loader2 className="animate-spin text-emerald-600" />;
  const m = s.mail_by_status;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testId="admin-stat-users" label="Users" value={s.users} />
        <Stat testId="admin-stat-revenue" label="Revenue" value={gbp(s.revenue_gbp)} accent />
        <Stat testId="admin-stat-claim-rate" label="Claim rate" value={`${s.claim_rate}%`} />
        <Stat testId="admin-stat-audit" label="Audit events" value={s.audit_events} />
        <Stat testId="admin-stat-awaiting-payment" label="Awaiting payment" value={m.awaiting_payment || 0} />
        <Stat testId="admin-stat-pending" label="Awaiting claim" value={m.pending_claim || 0} />
        <Stat testId="admin-stat-claimed" label="Claimed" value={m.claimed || 0} />
        <Stat testId="admin-stat-returned" label="Returned" value={m.returned || 0} />
        <Stat testId="admin-stat-sms-optouts" label="SMS opt-outs" value={s.sms_opt_outs || 0} />
      </div>
      <Panel title="Claim lifecycle">
        <p className="text-sm text-slate-500 mb-4">Runs automatically every hour: day-7 and day-12 reminders, and return-to-sender after 14 days. Trigger it manually here.</p>
        <button data-testid="admin-run-lifecycle-btn" onClick={run} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-sm font-semibold hover:bg-emerald-600 transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run lifecycle now
        </button>
      </Panel>
    </div>
  );
};

const Users = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/admin/users", { params: { q } }).then((r) => setRows(r.data)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-4">
      <div className="relative max-w-sm"><Search size={15} className="absolute left-3 top-3 text-slate-400" />
        <Input data-testid="admin-user-search" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-10" /></div>
      <Table testId="admin-users-table" rows={rows} cols={[
        ["Name", (u) => u.name], ["Email", (u) => u.email], ["Role", (u) => <span className="text-xs uppercase tracking-wider">{u.role}</span>],
        ["Verified", (u) => (u.email_verified ? "Yes" : "No")], ["Letters sent", (u) => u.sent_count],
        ["Enterprise · £/letter", (u) => <EnterpriseCell u={u} onSaved={(n) => setRows(rows.map((r) => (r.id === n.id ? { ...r, ...n } : r)))} />],
        ["Joined", (u) => mono(fmtDate(u.created_at))],
        ["2-step", (u) => <MfaResetCell u={u} onDone={() => setRows(rows.map((r) => (r.id === u.id ? { ...r, mfa_enabled: false } : r)))} />],
      ]} />
    </div>
  );
};

const MailTab = () => {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/mail", { params: { status } }).then((r) => setRows(r.data)); }, [status]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {["", "awaiting_payment", "pending_claim", "claimed", "returned"].map((s) => (
          <button key={s || "all"} data-testid={`admin-mail-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${status === s ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
            {s ? s.replace("_", " ") : "all"}
          </button>
        ))}
      </div>
      <Table testId="admin-mail-table" rows={rows} cols={[
        ["Ref", (m) => mono(m.reference)], ["Sender", (m) => m.sender_email], ["Recipient", (m) => `${m.recipient_name} · ${m.recipient_email}`],
        ["Status", (m) => <StatusBadge status={m.status} testId={`admin-mail-status-${m.reference}`} />], ["Notified", (m) => mono(fmtDate(m.notified_at))],
        ["Claim attempts", (m) => m.claim_attempts || 0], ["Expires", (m) => mono(fmtDate(m.expires_at))],
      ]} />
    </div>
  );
};

const AuditTab = () => {
  const [rows, setRows] = useState([]);
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/audit", { params: { limit: 200 } }).then((r) => setRows(r.data)); }, []);
  const verify = async () => {
    setBusy(true);
    try { setV((await api.get("/admin/audit/verify")).data); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-display font-semibold">Hash chain integrity</p>
            <p className="text-sm text-slate-500">Recomputes every SHA-256 link from the genesis record.</p>
          </div>
          <button data-testid="admin-verify-chain-btn" onClick={verify} disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Verify chain
          </button>
        </div>
        {v && (
          <div data-testid="admin-chain-result" className={`mt-4 rounded-lg border p-4 text-sm flex gap-3 items-start ${v.valid ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:border-emerald-900 dark:text-emerald-300" : "border-red-200 bg-red-50 text-red-700"}`}>
            {v.valid ? <ShieldCheck size={18} /> : <ShieldAlert size={18} />}
            <div>
              <p className="font-semibold">{v.valid ? `Chain intact · ${v.count} records verified` : `Chain broken at record #${v.broken_at_seq}`}</p>
              {v.head_hash && <p className="font-mono text-xs mt-1 break-all">head {v.head_hash}</p>}
            </div>
          </div>
        )}
      </Panel>
      <AuditTrail events={rows} showMail />
    </div>
  );
};

const Billing = () => {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/admin/billing").then((r) => setB(r.data)); }, []);
  if (!b) return <Loader2 className="animate-spin text-emerald-600" />;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 max-w-lg">
        <Stat testId="admin-billing-total" label="Total collected" value={gbp(b.total_gbp)} accent />
        <Stat testId="admin-billing-count" label="Paid sends" value={b.paid_count} />
      </div>
      <Table testId="admin-billing-table" rows={b.transactions} cols={[
        ["Date", (t) => mono(fmtDate(t.created_at))], ["User", (t) => t.user_email], ["Letter", (t) => mono(t.reference)],
        ["Amount", (t) => gbp(t.amount)], ["Status", (t) => t.payment_status], ["Session", (t) => <span className="font-mono text-[11px] text-slate-500">{t.session_id.slice(0, 24)}…</span>],
      ]} />
    </div>
  );
};

export default function Admin() {
  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="Administration" title="Admin dashboard" />
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          {["overview", "users", "mail", "audit", "billing", "usage", "enquiries", "requests", "brand", "idpolicy", "emails"].map((t) => <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize">{t === "idpolicy" ? "ID policy" : t === "brand" ? "Brand kit" : t === "requests" ? "Data requests" : t === "usage" ? "API usage" : t}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
        <TabsContent value="users" className="mt-6"><Users /></TabsContent>
        <TabsContent value="mail" className="mt-6"><MailTab /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditTab /></TabsContent>
        <TabsContent value="billing" className="mt-6"><Billing /></TabsContent>
        <TabsContent value="usage" className="mt-6"><AdminUsage /></TabsContent>
        <TabsContent value="enquiries" className="mt-6"><AdminEnquiries /></TabsContent>
        <TabsContent value="requests" className="mt-6"><AdminDataRequests /></TabsContent>
        <TabsContent value="brand" className="mt-6"><BrandKit /></TabsContent>
        <TabsContent value="idpolicy" className="mt-6"><AdminIdPolicy /></TabsContent>
        <TabsContent value="emails" className="mt-6"><AdminEmails /></TabsContent>
      </Tabs>
    </div>
  );
}
